/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * */

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { InsightActionsSlice } from '../../../../ts/state';
import {
  createTest,
  deleteTest,
  getTestResults,
  exportTestResults,
  getTestSettings
} from './actions';

const initialState: InsightActionsSlice = Object.freeze({
  createTest: {
    status: ApiStatus.IDLE,
    message: null,
    code: null,
    test: null,
    error: null
  },
  deleteTest: {
    status: ApiStatus.IDLE,
    code: null,
    message: null,
    error: null
  },
  testResults: {
    status: ApiStatus.IDLE,
    error: null,
    results: [],
    response_routing: {
      unrecognized: 0,
      skill_based_actions: 0,
      ai_guided_actions: 0,
      custom_built_actions: 0,
      conversational_search: 0,
      general_purpose_answering: 0
    },
    conversational_search: {
      extractiveness: 0,
      retrieval_confidence: 0,
      response_confidence: 0,
      citations_per_response: 0,
      response_length: 0
    },
    messages_total: 0,
    messages_on_page: 0,
    page_after_key: {
      first_timestamp: 0,
      conversation_id: null
    }
  },
  exportResults: {
    status: ApiStatus.IDLE,
    error: null
  },
  testSettings: {
    status: ApiStatus.IDLE,
    error: null,
    response: {
      test: null,
      status: null,
      message: null,
      processed: 0,
      total: 0,
      retrained_during_run: null,
      created: null,
      expires: null,
      settings: {
        disambiguation: null,
        no_action_matches: null,
        hap_filter: null,
        conversational_search: null,
        pre_gen_threshold: null,
        post_gen_threshold: null,
        response_length: null,
        general_purpose_answering: null,
        max_citations: null
      }
    }
  }
});

export const computeState = (
  updates?: Partial<InsightActionsSlice>
): InsightActionsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice & Reducer
const slice = createSlice({
  name: 'insightActions',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createTest.pending, (state: InsightActionsSlice) => {
        state.createTest.status = ApiStatus.LOADING;
        state.createTest.error = null;
      })
      .addCase(
        createTest.fulfilled,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.createTest = action.payload;
          state.createTest.status = ApiStatus.SUCCEEDED;
          state.createTest.error = null;
        }
      )
      .addCase(
        createTest.rejected,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.createTest.status = ApiStatus.ERROR;
          state.createTest.error = action.error;
        }
      )
      .addCase(deleteTest.pending, (state: InsightActionsSlice) => {
        state.deleteTest.status = ApiStatus.LOADING;
        state.deleteTest.error = null;
      })
      .addCase(
        deleteTest.fulfilled,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.deleteTest = action.payload;
          state.deleteTest.status = ApiStatus.SUCCEEDED;
          state.deleteTest.error = null;

          //Clear create state since it has been deleted
          state.createTest = { ...initialState.createTest };
        }
      )
      .addCase(
        deleteTest.rejected,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.deleteTest.status = ApiStatus.ERROR;
          state.deleteTest.error = action.error;
        }
      )
      .addCase(getTestResults.pending, (state: InsightActionsSlice) => {
        state.testResults.status = ApiStatus.LOADING;
        state.testResults.error = null;
      })
      .addCase(
        getTestResults.fulfilled,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.testResults = action.payload?.reports;
          state.testResults.status = ApiStatus.SUCCEEDED;
          state.testResults.error = null;
        }
      )
      .addCase(
        getTestResults.rejected,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.testResults.status = ApiStatus.ERROR;
          state.testResults.error = action.error;
        }
      )
      .addCase(exportTestResults.pending, (state: InsightActionsSlice) => {
        state.exportResults.status = ApiStatus.LOADING;
        state.exportResults.error = null;
      })
      .addCase(exportTestResults.fulfilled, (state: InsightActionsSlice) => {
        state.exportResults.status = ApiStatus.SUCCEEDED;
        state.exportResults.error = null;
      })
      .addCase(
        exportTestResults.rejected,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.exportResults.status = ApiStatus.ERROR;
          state.exportResults.error = action.error;
        }
      )
      .addCase(getTestSettings.pending, (state: InsightActionsSlice) => {
        state.testSettings.status = ApiStatus.LOADING;
        state.testSettings.error = null;
      })
      .addCase(
        getTestSettings.fulfilled,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.testSettings.response = action.payload;
          state.testSettings.status = ApiStatus.SUCCEEDED;
          state.testSettings.error = null;
        }
      )
      .addCase(
        getTestSettings.rejected,
        (state: InsightActionsSlice, action: AnyAction) => {
          state.testSettings.status = ApiStatus.ERROR;
          state.testSettings.error = action.error;
        }
      );
  }
});

export const { resetState } = slice.actions;

export default slice.reducer;
